/**
 * 內容載入器：用 Vite 的 import.meta.glob 在 build 時把 content/ 打包進來。
 * 新增案件只要丟一個 content/cases/XXX.json、新增任務丟一個 content/missions/<類型>/M-xxx.json，不需要改程式。
 */
import type { Flow, Minigame, GameConfig, Item, Product, Hazard, Rules, Role, Case, Floor, Npc, Pickup, Achievement, Ingredient, Board, PackItem, Semi, MenuItem, Tip, Mission, GameEvent } from './schema';
import { buildContent, type Content, type RawContent } from './build';
import { translateDeep } from '@/i18n';
import gameJson from '@content/game.json';
import rolesJson from '@content/roles.json';
import itemsJson from '@content/items.json';
import productsJson from '@content/orders/products.json';
import hazardsJson from '@content/hazards/hazards.json';
import rulesJson from '@content/rules/hygiene-rules.json';
import pickupsJson from '@content/pickups.json';
import achievementsJson from '@content/achievements.json';
import ingredientsJson from '@content/ingredients.json';
import boardsJson from '@content/boards.json';
import packJson from '@content/pack-items.json';
import semisJson from '@content/recipes/semis.json';
import menuJson from '@content/recipes/menu.json';
import tipsJson from '@content/tips.json';
import flowJson from '@content/flow.json';
import minigamesJson from '@content/minigames.json';
import equipActionsJson from '@content/equipment-actions.json';
import { tr } from '@/i18n';

const caseModules = import.meta.glob('../../content/cases/*.json', { eager: true, import: 'default' });
const floorModules = import.meta.glob('../../content/floors/*.json', { eager: true, import: 'default' });
const npcModules = import.meta.glob('../../content/npcs/*.json', { eager: true, import: 'default' });
const missionModules = import.meta.glob('../../content/missions/**/*.json', { eager: true, import: 'default' });

export type { Content };

export function loadContent(): Content {
  return buildContent(translateDeep({
    game: gameJson as unknown as GameConfig, roles: rolesJson as Role[], items: itemsJson as Item[], products: productsJson as Product[],
    hazards: hazardsJson as Hazard[], rules: rulesJson as Rules, pickups: pickupsJson as Pickup[], achievements: achievementsJson as Achievement[],
    ingredients: ingredientsJson as Ingredient[], boards: boardsJson as Board[], packItems: packJson as PackItem[], semis: semisJson as Semi[], menu: menuJson as MenuItem[], tips: tipsJson as Tip[], flow: flowJson as Flow, minigames: minigamesJson as Minigame[], equipActions: equipActionsJson as RawContent['equipActions'],
    cases: Object.values(caseModules) as Case[], floors: Object.values(floorModules) as Floor[], npcs: (Object.values(npcModules) as Npc[][]).flat(),
    missionFiles: Object.values(missionModules) as (Mission | GameEvent)[],
  }));
}

/** 開發模式才做執行期驗證（正式版用 build 前的 validate 腳本把關） */
export async function devValidate(): Promise<void> {
  if (!import.meta.env.DEV) return;
  const { validateAll } = await import('./validate');
  const errors = validateAll(loadContent());
  if (errors.length) console.warn(tr("[content] 驗證失敗：\n{0}", errors.join('\n')));
  else console.info(tr("[content] 內容驗證通過"));
}
